import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError, setStoredToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch((err) => {
        // 401 is expected when no session exists; only log other errors
        if (err?.response?.status !== 401) console.warn("Initial auth check failed", err);
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setStoredToken(data.access_token);
      // Pull the full user (incl. has_access) so paywall gating is correct.
      let full = data;
      try { full = (await api.get("/auth/me")).data; } catch { /* fall back to login payload */ }
      setUser(full);
      setError("");
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Login failed");
      return false;
    }
  };

  const demoLogin = async () => {
    try {
      const { data } = await api.post("/auth/demo-login");
      if (data.access_token) setStoredToken(data.access_token);
      // Pull the full user (incl. has_access) so paywall gating is correct.
      let full = data;
      try { full = (await api.get("/auth/me")).data; } catch { /* fall back to demo payload */ }
      setUser(full);
      setError("");
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not start demo");
      return false;
    }
  };

  const register = async (email, password, name, acceptTos = true, inviteCode = "", source = undefined) => {
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name,
        accept_tos: acceptTos,
        invite_code: inviteCode,
        ...(source ? { source } : {}),
      });
      if (data.access_token) setStoredToken(data.access_token);
      // Pull the full user (incl. has_access) so paywall gating is correct.
      let full = data;
      try { full = (await api.get("/auth/me")).data; } catch { /* fall back to register payload */ }
      setUser(full);
      setError("");
      return { ok: true, isAdvisor: !!data.is_advisor };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Surface server-side beta-gate message verbatim instead of stringifying the object.
      if (detail && typeof detail === "object" && detail.error === "invite_required") {
        setError(detail.message);
        return { ok: false, detail };
      }
      setError(formatApiError(detail) || "Registration failed");
      return { ok: false, detail };
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch((err) => {
      console.warn("Logout API failed; clearing local state anyway", err);
    });
    setStoredToken(null);
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      console.warn("refreshUser failed", err);
      return null;
    }
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, demoLogin, register, logout, setError, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
